import type { ClientUiPublic } from '../../api/platformConfig'
import {
  COMIC_ART_STYLE_OPTIONS,
  COMIC_TONE_OPTIONS,
  type ComicArtStylePreset,
  type ComicProjectCreative,
  type ComicTonePreset,
  normalizeComicProjectCreative,
  validateComicProjectCreative,
} from '../../lib/comicCreativeBible'
import {
  panelAspectSelectOptions,
  panelAspectSelectValue,
} from '../../lib/comicProjectSettings'
import { ChromeComposerTextarea } from '../ChromeComposerTextarea'
import { SelectMenu } from '../SelectMenu'
import { SettingsGroup, SettingsOptionGroup } from '../SettingsHierarchy'

type Props = {
  angular: boolean
  clientUi: ClientUiPublic
  modelId: string
  platformId: string
  value: ComicProjectCreative
  onChange: (next: ComicProjectCreative) => void
}

export function ComicCreativeSettingsForm({
  angular,
  value,
  onChange,
}: Props) {
  const patch = (partial: Partial<ComicProjectCreative>) => {
    onChange(normalizeComicProjectCreative({ ...value, ...partial }))
  }

  const panelAspect = panelAspectSelectValue(value.panelAspect)
  const creativeValidation = validateComicProjectCreative(value)

  return (
    <SettingsGroup angular={angular} title="画册基调">
      <section className="min-w-0">
        <label className="mb-1.5 block text-xs font-semibold text-ink">单页比例</label>
        <SelectMenu
          id="comic-creative-panel-aspect"
          ariaLabel="单页比例"
          value={panelAspect}
          onChange={(next) => patch({ panelAspect: next })}
          options={panelAspectSelectOptions()}
          angular={angular}
        />
        {!creativeValidation.ok ? (
          <p className="mt-2 text-xs text-[color-mix(in_srgb,var(--ink)_75%,var(--color-warning)_25%)]" role="alert">
            {creativeValidation.message}
          </p>
        ) : null}
      </section>

      <SettingsOptionGroup
        angular={angular}
        title="画风"
        ariaLabel="画风预设"
        options={COMIC_ART_STYLE_OPTIONS.map((opt) => ({
          value: opt.value,
          label: opt.label,
          description: opt.hint,
        }))}
        value={value.artStyle}
        onChange={(v) => patch({ artStyle: v as ComicArtStylePreset })}
        customLabel="画风说明"
        customPlaceholder="例如：细线、网点阴影、90 年代少女漫"
        customText={value.artStyleNotes}
        customMaxLength={400}
        onCustomTextChange={(artStyleNotes) => patch({ artStyleNotes })}
      />

      <SettingsOptionGroup
        angular={angular}
        title="叙事基调"
        ariaLabel="叙事基调预设"
        options={COMIC_TONE_OPTIONS.map((opt) => ({
          value: opt.value,
          label: opt.label,
          description: opt.hint,
        }))}
        value={value.tone}
        onChange={(v) => patch({ tone: v as ComicTonePreset })}
        customLabel="基调说明"
        customPlaceholder="例如：日常系、少量喜剧节奏"
        customText={value.toneNotes}
        customMaxLength={300}
        onCustomTextChange={(toneNotes) => patch({ toneNotes })}
      />

      <section className="space-y-3 border-t border-line pt-5">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink">作品细节</p>

        <label className="block text-sm font-semibold text-ink">
          色调与光影
          <ChromeComposerTextarea
            angular={angular}
            variant="form"
            value={value.colorPalette}
            onChange={(e) => patch({ colorPalette: e.target.value })}
            placeholder="例如：暖色、低饱和、夜景霓虹"
            maxLength={200}
            textareaClassName="min-h-[2.5rem]"
          />
        </label>

        <label className="block text-sm font-semibold text-ink">
          世界观 / 时代背景
          <ChromeComposerTextarea
            angular={angular}
            variant="form"
            value={value.worldSetting}
            onChange={(e) => patch({ worldSetting: e.target.value })}
            placeholder="例如：近未来都市、校园、古代仙侠"
            maxLength={400}
          />
        </label>

        <label className="block text-sm font-semibold text-ink">
          避免元素
          <ChromeComposerTextarea
            angular={angular}
            variant="form"
            value={value.avoidNotes}
            onChange={(e) => patch({ avoidNotes: e.target.value })}
            placeholder="例如：不要写实照片感、不要水印、不要多余肢体"
            maxLength={400}
            textareaClassName="min-h-[2.5rem]"
          />
        </label>
      </section>
    </SettingsGroup>
  )
}
